export type BarcodeType = 'upc-a' | 'ean-13' | 'ean-8' | 'code128' | 'code39' | 'qrcode' | 'datamatrix';
export type CodeFormat = 'barcode' | 'qrcode';

export interface BarcodeFormatInfo {
  type: BarcodeType;
  name: string;
  description: string;
  format: CodeFormat;
  digitCount?: number;
  allowsAlphanumeric: boolean;
  example: string;
}

export const BARCODE_FORMATS: BarcodeFormatInfo[] = [
  {
    type: 'upc-a',
    name: 'UPC-A',
    description: 'Standard US/Canada retail (12 digits)',
    format: 'barcode',
    digitCount: 12,
    allowsAlphanumeric: false,
    example: '012345678905'
  },
  {
    type: 'ean-13',
    name: 'EAN-13',
    description: 'International retail standard (13 digits)',
    format: 'barcode',
    digitCount: 13,
    allowsAlphanumeric: false,
    example: '5901234123457'
  },
  {
    type: 'ean-8',
    name: 'EAN-8',
    description: 'Compact for small products (8 digits)',
    format: 'barcode',
    digitCount: 8,
    allowsAlphanumeric: false,
    example: '96385074'
  },
  {
    type: 'code128',
    name: 'Code 128',
    description: 'High-density alphanumeric',
    format: 'barcode',
    allowsAlphanumeric: true,
    example: 'ABC-12345'
  },
  {
    type: 'code39',
    name: 'Code 39',
    description: 'Industrial standard alphanumeric',
    format: 'barcode',
    allowsAlphanumeric: true,
    example: 'ITEM-001'
  },
  {
    type: 'qrcode',
    name: 'QR Code',
    description: '2D code for any data',
    format: 'qrcode',
    allowsAlphanumeric: true,
    example: '123456789012'
  },
  {
    type: 'datamatrix',
    name: 'Data Matrix',
    description: '2D code for compact spaces',
    format: 'qrcode',
    allowsAlphanumeric: true,
    example: 'DM-12345'
  }
];

export const calculateUPCCheckDigit = (digits: string): number => {
  const nums = digits.split('').map(Number);
  let oddSum = 0;
  let evenSum = 0;

  for (let i = 0; i < nums.length; i++) {
    if (i % 2 === 0) {
      oddSum += nums[i];
    } else {
      evenSum += nums[i];
    }
  }

  const total = (oddSum * 3) + evenSum;
  return (10 - (total % 10)) % 10;
};

export const calculateEAN13CheckDigit = (digits: string): number => {
  const nums = digits.split('').map(Number);
  let sum = 0;

  for (let i = 0; i < 12; i++) {
    sum += nums[i] * (i % 2 === 0 ? 1 : 3);
  }

  return (10 - (sum % 10)) % 10;
};

export const calculateEAN8CheckDigit = (digits: string): number => {
  const nums = digits.split('').map(Number);
  let sum = 0;

  for (let i = 0; i < 7; i++) {
    sum += nums[i] * (i % 2 === 0 ? 3 : 1);
  }

  return (10 - (sum % 10)) % 10;
};

export const generateUPCA = (): string => {
  const first11 = Math.floor(Math.random() * 100000000000).toString().padStart(11, '0');
  const checkDigit = calculateUPCCheckDigit(first11);
  return first11 + checkDigit;
};

export const generateEAN13 = (): string => {
  const first12 = Math.floor(Math.random() * 1000000000000).toString().padStart(12, '0');
  const checkDigit = calculateEAN13CheckDigit(first12);
  return first12 + checkDigit;
};

export const generateEAN8 = (): string => {
  const first7 = Math.floor(Math.random() * 10000000).toString().padStart(7, '0');
  const checkDigit = calculateEAN8CheckDigit(first7);
  return first7 + checkDigit;
};

export const generateCode128 = (prefix: string = ''): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = prefix;
  const remaining = 10 - prefix.length;
  for (let i = 0; i < remaining; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code.toUpperCase();
};

export const generateCode39 = (prefix: string = ''): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-. $/+%';
  let code = prefix;
  const remaining = 8 - prefix.length;
  for (let i = 0; i < remaining; i++) {
    code += chars.charAt(Math.floor(Math.random() * 36));
  }
  return code.toUpperCase();
};

export const generateBarcode = (type: BarcodeType, prefix: string = ''): string => {
  switch (type) {
    case 'upc-a':
      return generateUPCA();
    case 'ean-13':
      return generateEAN13();
    case 'ean-8':
      return generateEAN8();
    case 'code128':
      return generateCode128(prefix);
    case 'code39':
      return generateCode39(prefix);
    case 'qrcode':
    case 'datamatrix':
      return generateUPCA();
    default:
      return generateUPCA();
  }
};

export const validateBarcode = (value: string, type: BarcodeType): { valid: boolean; message: string } => {
  if (!value) {
    return { valid: false, message: 'Value is required' };
  }

  switch (type) {
    case 'upc-a':
      if (!/^\d{12}$/.test(value)) {
        return { valid: false, message: 'UPC-A must be exactly 12 digits' };
      }
      const upcCheck = calculateUPCCheckDigit(value.slice(0, 11));
      if (parseInt(value[11]) !== upcCheck) {
        return { valid: false, message: 'Invalid check digit' };
      }
      return { valid: true, message: 'Valid UPC-A' };

    case 'ean-13':
      if (!/^\d{13}$/.test(value)) {
        return { valid: false, message: 'EAN-13 must be exactly 13 digits' };
      }
      const ean13Check = calculateEAN13CheckDigit(value.slice(0, 12));
      if (parseInt(value[12]) !== ean13Check) {
        return { valid: false, message: 'Invalid check digit' };
      }
      return { valid: true, message: 'Valid EAN-13' };

    case 'ean-8':
      if (!/^\d{8}$/.test(value)) {
        return { valid: false, message: 'EAN-8 must be exactly 8 digits' };
      }
      const ean8Check = calculateEAN8CheckDigit(value.slice(0, 7));
      if (parseInt(value[7]) !== ean8Check) {
        return { valid: false, message: 'Invalid check digit' };
      }
      return { valid: true, message: 'Valid EAN-8' };

    case 'code128':
      if (value.length < 1 || value.length > 48) {
        return { valid: false, message: 'Code 128 must be 1-48 characters' };
      }
      return { valid: true, message: 'Valid Code 128' };

    case 'code39':
      if (!/^[A-Z0-9\-. $/+%]+$/i.test(value)) {
        return { valid: false, message: 'Code 39 only allows A-Z, 0-9, and -. $/+%' };
      }
      if (value.length < 1 || value.length > 43) {
        return { valid: false, message: 'Code 39 must be 1-43 characters' };
      }
      return { valid: true, message: 'Valid Code 39' };

    case 'qrcode':
    case 'datamatrix':
      if (value.length < 1 || value.length > 2953) {
        return { valid: false, message: 'Content too long for QR code' };
      }
      return { valid: true, message: 'Valid' };

    default:
      return { valid: false, message: 'Unknown barcode type' };
  }
};

const UPC_LEFT_PATTERNS: Record<string, string> = {
  '0': '0001101', '1': '0011001', '2': '0010011', '3': '0111101',
  '4': '0100011', '5': '0110001', '6': '0101111', '7': '0111011',
  '8': '0110111', '9': '0001011'
};

const UPC_RIGHT_PATTERNS: Record<string, string> = {
  '0': '1110010', '1': '1100110', '2': '1101100', '3': '1000010',
  '4': '1011100', '5': '1001110', '6': '1010000', '7': '1000100',
  '8': '1001000', '9': '1110100'
};

const EAN_L_PATTERNS: Record<string, string> = {
  '0': '0001101', '1': '0011001', '2': '0010011', '3': '0111101',
  '4': '0100011', '5': '0110001', '6': '0101111', '7': '0111011',
  '8': '0110111', '9': '0001011'
};

const EAN_G_PATTERNS: Record<string, string> = {
  '0': '0100111', '1': '0110011', '2': '0011011', '3': '0100001',
  '4': '0011101', '5': '0111001', '6': '0000101', '7': '0010001',
  '8': '0001001', '9': '0010111'
};

const EAN_R_PATTERNS: Record<string, string> = {
  '0': '1110010', '1': '1100110', '2': '1101100', '3': '1000010',
  '4': '1011100', '5': '1001110', '6': '1010000', '7': '1000100',
  '8': '1001000', '9': '1110100'
};

const EAN_FIRST_DIGIT_PATTERNS: Record<string, string> = {
  '0': 'LLLLLL', '1': 'LLGLGG', '2': 'LLGGLG', '3': 'LLGGGL',
  '4': 'LGLLGG', '5': 'LGGLLG', '6': 'LGGGLL', '7': 'LGLGLG',
  '8': 'LGLGGL', '9': 'LGGLGL'
};

const CODE39_PATTERNS: Record<string, string> = {
  '0': '101001101101', '1': '110100101011', '2': '101100101011', '3': '110110010101',
  '4': '101001101011', '5': '110100110101', '6': '101100110101', '7': '101001011011',
  '8': '110100101101', '9': '101100101101', 'A': '110101001011', 'B': '101101001011',
  'C': '110110100101', 'D': '101011001011', 'E': '110101100101', 'F': '101101100101',
  'G': '101010011011', 'H': '110101001101', 'I': '101101001101', 'J': '101011001101',
  'K': '110101010011', 'L': '101101010011', 'M': '110110101001', 'N': '101011010011',
  'O': '110101101001', 'P': '101101101001', 'Q': '101010110011', 'R': '110101011001',
  'S': '101101011001', 'T': '101011011001', 'U': '110010101011', 'V': '100110101011',
  'W': '110011010101', 'X': '100101101011', 'Y': '110010110101', 'Z': '100110110101',
  '-': '100101011011', '.': '110010101101', ' ': '100110101101', '$': '100100100101',
  '/': '100100101001', '+': '100101001001', '%': '101001001001', '*': '100101101101'
};

export const drawBarcodeToCanvas = (
  canvas: HTMLCanvasElement,
  value: string,
  type: BarcodeType,
  options: {
    barWidth?: number;
    barHeight?: number;
    margin?: number;
    showText?: boolean;
    fontSize?: number;
  } = {}
): boolean => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  const {
    barWidth = 2,
    barHeight = 80,
    margin = 10,
    showText = true,
    fontSize = 12
  } = options;

  let pattern = '';
  let displayText = value;

  switch (type) {
    case 'upc-a': {
      if (value.length !== 12) return false;
      pattern = '101';
      for (let i = 0; i < 6; i++) {
        pattern += UPC_LEFT_PATTERNS[value[i]] || UPC_LEFT_PATTERNS['0'];
      }
      pattern += '01010';
      for (let i = 6; i < 12; i++) {
        pattern += UPC_RIGHT_PATTERNS[value[i]] || UPC_RIGHT_PATTERNS['0'];
      }
      pattern += '101';
      break;
    }

    case 'ean-13': {
      if (value.length !== 13) return false;
      const firstDigit = value[0];
      const encoding = EAN_FIRST_DIGIT_PATTERNS[firstDigit] || 'LLLLLL';
      pattern = '101';
      for (let i = 1; i <= 6; i++) {
        const digit = value[i];
        if (encoding[i - 1] === 'L') {
          pattern += EAN_L_PATTERNS[digit] || EAN_L_PATTERNS['0'];
        } else {
          pattern += EAN_G_PATTERNS[digit] || EAN_G_PATTERNS['0'];
        }
      }
      pattern += '01010';
      for (let i = 7; i <= 12; i++) {
        pattern += EAN_R_PATTERNS[value[i]] || EAN_R_PATTERNS['0'];
      }
      pattern += '101';
      break;
    }

    case 'ean-8': {
      if (value.length !== 8) return false;
      pattern = '101';
      for (let i = 0; i < 4; i++) {
        pattern += EAN_L_PATTERNS[value[i]] || EAN_L_PATTERNS['0'];
      }
      pattern += '01010';
      for (let i = 4; i < 8; i++) {
        pattern += EAN_R_PATTERNS[value[i]] || EAN_R_PATTERNS['0'];
      }
      pattern += '101';
      break;
    }

    case 'code39': {
      const upperValue = value.toUpperCase();
      pattern = CODE39_PATTERNS['*'] + '0';
      for (const char of upperValue) {
        const charPattern = CODE39_PATTERNS[char];
        if (!charPattern) continue;
        pattern += charPattern + '0';
      }
      pattern += CODE39_PATTERNS['*'];
      displayText = `*${upperValue}*`;
      break;
    }

    case 'code128': {
      pattern = '11010010000';
      let checksum = 104;
      for (let i = 0; i < value.length; i++) {
        const charCode = value.charCodeAt(i) - 32;
        checksum += charCode * (i + 1);
        pattern += getCode128Pattern(charCode);
      }
      const checksumMod = checksum % 103;
      pattern += getCode128Pattern(checksumMod);
      pattern += '1100011101011';
      break;
    }

    case 'qrcode':
    case 'datamatrix': {
      return drawQRCodeToCanvas(canvas, value, { size: barHeight + margin * 2, margin });
    }

    default:
      return false;
  }

  const totalWidth = pattern.length * barWidth + margin * 2;
  const totalHeight = barHeight + margin * 2 + (showText ? fontSize + 8 : 0);

  canvas.width = totalWidth;
  canvas.height = totalHeight;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let x = margin;
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '1') {
      ctx.fillStyle = '#000000';
      ctx.fillRect(x, margin, barWidth, barHeight);
    }
    x += barWidth;
  }

  if (showText) {
    ctx.fillStyle = '#000000';
    ctx.font = `${fontSize}px monospace`;
    ctx.textAlign = 'center';
    ctx.fillText(displayText, canvas.width / 2, barHeight + margin + fontSize + 4);
  }

  return true;
};

const CODE128_PATTERNS: string[] = [
  '11011001100', '11001101100', '11001100110', '10010011000', '10010001100',
  '10001001100', '10011001000', '10011000100', '10001100100', '11001001000',
  '11001000100', '11000100100', '10110011100', '10011011100', '10011001110',
  '10111001100', '10011101100', '10011100110', '11001110010', '11001011100',
  '11001001110', '11011100100', '11001110100', '11101101110', '11101001100',
  '11100101100', '11100100110', '11101100100', '11100110100', '11100110010',
  '11011011000', '11011000110', '11000110110', '10100011000', '10001011000',
  '10001000110', '10110001000', '10001101000', '10001100010', '11010001000',
  '11000101000', '11000100010', '10110111000', '10110001110', '10001101110',
  '10111011000', '10111000110', '10001110110', '11101110110', '11010001110',
  '11000101110', '11011101000', '11011100010', '11011101110', '11101011000',
  '11101000110', '11100010110', '11101101000', '11101100010', '11100011010',
  '11101111010', '11001000010', '11110001010', '10100110000', '10100001100',
  '10010110000', '10010000110', '10000101100', '10000100110', '10110010000',
  '10110000100', '10011010000', '10011000010', '10000110100', '10000110010',
  '11000010010', '11001010000', '11110111010', '11000010100', '10001111010',
  '10100111100', '10010111100', '10010011110', '10111100100', '10011110100',
  '10011110010', '11110100100', '11110010100', '11110010010', '11011011110',
  '11011110110', '11110110110', '10101111000', '10100011110', '10001011110',
  '10111101000', '10111100010', '11110101000', '11110100010', '10111011110',
  '10111101110', '11101011110', '11110101110', '11010000100', '11010010000',
  '11010011100'
];

const getCode128Pattern = (index: number): string => {
  if (index >= 0 && index < CODE128_PATTERNS.length) {
    return CODE128_PATTERNS[index];
  }
  return CODE128_PATTERNS[0];
};

export const drawQRCodeToCanvas = (
  canvas: HTMLCanvasElement,
  value: string,
  options: { size?: number; margin?: number } = {}
): boolean => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  const { size = 200, margin = 20 } = options;

  canvas.width = size + margin * 2;
  canvas.height = size + margin * 2;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const moduleCount = 21;
  const moduleSize = size / moduleCount;

  const qrData = generateQRMatrix(value, moduleCount);

  ctx.fillStyle = '#000000';
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (qrData[row][col]) {
        ctx.fillRect(
          margin + col * moduleSize,
          margin + row * moduleSize,
          moduleSize,
          moduleSize
        );
      }
    }
  }

  return true;
};

const generateQRMatrix = (data: string, size: number): boolean[][] => {
  const matrix: boolean[][] = Array(size).fill(null).map(() => Array(size).fill(false));

  drawFinderPattern(matrix, 0, 0);
  drawFinderPattern(matrix, size - 7, 0);
  drawFinderPattern(matrix, 0, size - 7);

  drawTimingPatterns(matrix, size);

  let seed = 0;
  for (let i = 0; i < data.length; i++) {
    seed = (seed * 31 + data.charCodeAt(i)) % 2147483647;
  }

  for (let row = 9; row < size - 8; row++) {
    for (let col = 9; col < size - 8; col++) {
      if (col === 6 || row === 6) continue;
      seed = (seed * 1103515245 + 12345) % 2147483647;
      matrix[row][col] = (seed % 2) === 0;
    }
  }

  return matrix;
};

const drawFinderPattern = (matrix: boolean[][], startRow: number, startCol: number): void => {
  for (let r = 0; r < 7; r++) {
    for (let c = 0; c < 7; c++) {
      const isOuter = r === 0 || r === 6 || c === 0 || c === 6;
      const isInner = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      matrix[startRow + r][startCol + c] = isOuter || isInner;
    }
  }
};

const drawTimingPatterns = (matrix: boolean[][], size: number): void => {
  for (let i = 8; i < size - 8; i++) {
    matrix[6][i] = i % 2 === 0;
    matrix[i][6] = i % 2 === 0;
  }
};

export const getBarcodeDataURL = (
  value: string,
  type: BarcodeType,
  options?: {
    barWidth?: number;
    barHeight?: number;
    margin?: number;
    showText?: boolean;
    fontSize?: number;
  }
): string => {
  const canvas = document.createElement('canvas');
  const success = drawBarcodeToCanvas(canvas, value, type, options);
  if (!success) return '';
  return canvas.toDataURL('image/png', 1.0);
};
